import * as d3 from 'd3';
import { getArtifactTypes, isAnonymized } from '@/lib/filters';

export function createLandscape(container, papers, { artifactColors, onPaperClick } = {}) {
  const width = container.clientWidth || 900;

  const years = papers.map((p) => p.metadata?.year).filter(Boolean);
  const yearExtent = d3.extent(years);
  const yearDomain =
    yearExtent[0] === yearExtent[1] ? [yearExtent[0] - 1, yearExtent[0] + 1] : yearExtent;

  const trackCounts = new Map();
  papers.forEach((p) => {
    const track = p.metadata?.track;
    if (track) trackCounts.set(track, (trackCounts.get(track) || 0) + 1);
  });
  const tracks = [...trackCounts.entries()].sort((a, b) => b[1] - a[1]).map(([track]) => track);

  const longestLabel = tracks.reduce((max, t) => Math.max(max, t.length), 0);
  const margin = { top: 20, right: 24, bottom: 40, left: 24 + longestLabel * 6.2 };

  const height = Math.max(360, margin.top + margin.bottom + tracks.length * 32);

  const x = d3
    .scaleLinear()
    .domain(yearDomain)
    .range([margin.left, width - margin.right])
    .nice();

  const y = d3
    .scaleBand()
    .domain(tracks)
    .range([height - margin.bottom, margin.top])
    .paddingOuter(0.5);

  const svg = d3
    .select(container)
    .append('svg')
    .attr('width', width)
    .attr('height', height)
    .attr('viewBox', [0, 0, width, height]);

  svg
    .append('g')
    .attr('transform', `translate(0,${height - margin.bottom})`)
    .call(d3.axisBottom(x).tickFormat(d3.format('d')).ticks(Math.min(yearDomain[1] - yearDomain[0] + 1, 10)))
    .call((g) => g.selectAll('text').attr('fill', 'var(--text-muted)'))
    .call((g) => g.selectAll('line,path').attr('stroke', 'var(--border)'));

  svg
    .append('g')
    .attr('transform', `translate(${margin.left},0)`)
    .call(d3.axisLeft(y))
    .call((g) => g.selectAll('text').attr('fill', 'var(--text-muted)'))
    .call((g) => g.selectAll('line,path').attr('stroke', 'var(--border)'));

  const jitterX = () => (Math.random() - 0.5) * 0.5;
  const jitterY = () => (Math.random() - 0.5) * y.bandwidth() * 0.7;

  const points = papers
    .filter((p) => p.metadata?.year && p.metadata?.track)
    .map((p) => {
      const artifactTypes = getArtifactTypes(p);
      return {
        paper: p,
        x: p.metadata.year + jitterX() * 0.3,
        y: y(p.metadata.track) + y.bandwidth() / 2 + jitterY(),
        color: artifactColors[artifactTypes[0]] || '#88c0d0',
        radius: 4 + Math.min(p.contribution?.artifacts?.length || 1, 4) * 1.5,
        anonymized: isAnonymized(p),
        userAdded: Boolean(p._userAdded),
      };
    });

  const tooltip = d3
    .select(container)
    .append('div')
    .attr('class', 'landscape-tooltip')
    .style('opacity', 0);

  const pointsLayer = svg.append('g');

  // Dashed accent ring behind papers added by the visitor, same treatment
  // as the relationship graph.
  pointsLayer
    .selectAll('circle.user-added-ring')
    .data(points.filter((d) => d.userAdded))
    .join('circle')
    .attr('class', 'user-added-ring')
    .attr('cx', (d) => x(d.x))
    .attr('cy', (d) => d.y)
    .attr('r', (d) => d.radius + 4)
    .attr('fill', 'none')
    .attr('stroke', 'var(--accent)')
    .attr('stroke-width', 1.5)
    .attr('stroke-dasharray', '3,2')
    .style('pointer-events', 'none');

  const circles = pointsLayer
    .selectAll('circle.landscape-point')
    .data(points)
    .join('circle')
    .attr('class', 'landscape-point')
    .attr('cx', (d) => x(d.x))
    .attr('cy', (d) => d.y)
    .attr('r', (d) => d.radius)
    .attr('fill', (d) => d.color)
    .attr('fill-opacity', (d) => (d.anonymized ? 0.28 : 0.75))
    .attr('stroke', (d) => (d.anonymized ? 'var(--bg)' : 'var(--text)'))
    .attr('stroke-width', 1)
    .style('cursor', 'pointer')
    .on('mouseenter', (event, d) => {
      if (d.anonymized) return;
      const addedTag = d.userAdded ? ' <span style="color:var(--accent-text)">· Added by you</span>' : '';
      tooltip
        .style('opacity', 1)
        .html(`<strong>${d.paper.metadata.title}</strong>${addedTag}<br/>${d.paper.metadata.year} · ${d.paper.metadata.track}`);
    })
    .on('mousemove', (event) => {
      const [mx, my] = d3.pointer(event, container);
      const tooltipWidth = tooltip.node().offsetWidth;
      const overflowsRight = mx + 12 + tooltipWidth > container.clientWidth;
      const left = overflowsRight ? mx - 12 - tooltipWidth : mx + 12;
      tooltip.style('left', `${left}px`).style('top', `${my + 12}px`);
    })
    .on('mouseleave', () => tooltip.style('opacity', 0))
    .on('click', (event, d) => {
      if (onPaperClick) onPaperClick(d.paper._id);
    });

  return {
    destroy: () => {
      circles.interrupt();
      tooltip.remove();
      svg.remove();
    },
  };
}
